import React, { useCallback, useEffect, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Trash2, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { Card, CardContent } from '../../../components/shadcn/ui/card';
import { Button } from '../../../components/shadcn/ui/button';
import { Input } from '../../../components/shadcn/ui/input';
import { Badge } from '../../../components/shadcn/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/shadcn/ui/table';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../components/shadcn/ui/sheet';

interface CohortOption { key: string; label: string; description?: string }
interface CohortMeta {
  applied: string;
  label?: string | null;
  description?: string | null;
  matched: number;
  censusTotal: number;
  available: CohortOption[];
}

export interface EntityColumn {
  key: string;
  label: string;
  format?: (value: any, row: any) => React.ReactNode;
}

interface AdminEntityListProps {
  entity: string;
  title: string;
  columns: EntityColumn[];
  searchPlaceholder?: string;
}

const fmtValue = (v: any): React.ReactNode => {
  if (v === null || v === undefined || v === '') return <span className="text-muted-foreground">—</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="text-muted-foreground">—</span>;
  return String(v);
};

export default function AdminEntityList({ entity, title, columns, searchPlaceholder }: AdminEntityListProps) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<{ page: number; pageSize: number; total: number } | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  // undefined = "use the server's default"; the server decides which cohort a
  // list opens on, so a new entity does not need a matching change here.
  const [cohort, setCohort] = useState<string | undefined>(undefined);
  const [cohortMeta, setCohortMeta] = useState<CohortMeta | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (search) qp.set('q', search);
      if (cohort) qp.set('cohort', cohort);
      const res: any = await api.get(`/emerge/${entity}?${qp.toString()}`);
      setRows(res?.data || []);
      setPagination(res?.pagination || null);
      setCohortMeta(res?.cohort || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entity, page, search, cohort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setCohort(undefined); setCohortMeta(null); }, [entity]);

  const remove = async (id: string) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/emerge/${entity}/${id}`);
      setSelected(null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          {/* Always show the filtered count against the census. Filtering hides
              records; it must never read as though they were deleted. */}
          <p className="mt-1 text-sm text-muted-foreground">
            {(cohortMeta || pagination) && (
              <Badge variant="secondary" className="tabular-nums">
                {cohortMeta
                  ? `${cohortMeta.matched.toLocaleString()} of ${cohortMeta.censusTotal.toLocaleString()} records`
                  : `${pagination!.total.toLocaleString()} records`}
              </Badge>
            )}
          </p>
          {cohortMeta?.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{cohortMeta.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cohortMeta && cohortMeta.available.length > 1 && (
            <div className="flex items-center rounded-md border border-input overflow-hidden" role="group" aria-label="Cohort">
              {cohortMeta.available.map((opt) => {
                const active = cohortMeta.applied === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    title={opt.description}
                    aria-pressed={active}
                    onClick={() => { setCohort(opt.key); setPage(1); }}
                    className={`px-3 py-1.5 text-xs whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-safe:transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-transparent text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="search"
              aria-label={searchPlaceholder || 'Search'}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={searchPlaceholder || 'Search...'}
              className="pl-9 w-64"
            />
          </div>
          <Button variant="outline" size="icon" onClick={() => load()} disabled={loading} aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'motion-safe:animate-spin' : ''}`} aria-hidden="true" />
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Table className="min-w-[700px]" aria-busy={loading || undefined}>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key} className="text-xs">{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelected(row)}>
                  {columns.map((c, i) => (
                    <TableCell key={c.key} className="py-2.5">
                      {/* The first cell is the row's keyboard door to the record (a click anywhere on the row opens it too). */}
                      {i === 0 ? (
                        <button type="button" className="rounded-sm text-left font-medium underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={(e) => { e.stopPropagation(); setSelected(row); }}>
                          {c.format ? c.format(row[c.key], row) : fmtValue(row[c.key])}
                        </button>
                      ) : (c.format ? c.format(row[c.key], row) : fmtValue(row[c.key]))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={columns.length} className="p-8 text-center text-muted-foreground">No records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {pagination.page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      {/* The record: a modal side sheet (Radix Dialog). Named, focus held inside, Escape and the backdrop close it,
          and focus returns to the row's button. Focus opens on the sheet itself, not its first button (Delete). */}
      <Sheet open={selected !== null} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md"
          onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus(); }}>
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{title.replace(/s$/, '')} record</SheetTitle>
                <SheetDescription className="sr-only">Every field of this record as stored.</SheetDescription>
              </SheetHeader>
              <dl className="mt-4 flex flex-col gap-3">
                {Object.entries(selected).map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-0.5 border-b border-border pb-2">
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd className="m-0 text-sm break-words">{fmtValue(v)}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-destructive/40 text-[var(--tide-status-danger)] hover:bg-[var(--tide-status-danger-bg)]"
                  disabled={deleting}
                  onClick={() => remove(selected.id)}
                >
                  <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
